import type {ReduxStore} from 'src/reducers';
import IndexStore from 'src/stores';
import * as React from 'react';
import {Provider} from 'react-redux';
import {getCurrentLanguageForExternalApps} from 'src/utils/language-support';
import ErrorBoundary from 'src/components/500/boundary';
import AppRoutes from 'src/reroutes/referrer-portal';
import {
  ToastContainer,
  ToastContainer as GenesisToastContainer,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {DndProvider} from 'react-dnd-cjs';
import HTML5Backend from 'react-dnd-html5-backend-cjs';
import {Helmet} from 'react-helmet';
import FluxRoot from 'src/flux/root';
import ModalRoot from 'src/components/modals/modal-root';
import {ContextMenuRoot} from 'src/components/lib/context-menu';
import DragLayer from 'src/components/lib/dnd/layer';
import {isDevOrStage} from 'src/utils/env';
import {MouseTipRoot} from 'src/components/lib/mouse-tip/mouse-tip';
import 'src/components/app.global.css';
import styles from 'src/components/referrer-portal/app.css';

import I18nContext from 'src/contexts/i18n-context';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import labelI18nInstance, {
  ensureInitialized,
} from 'src/components/lib/i18n/new-i18n';


export default function ReferrerPortalApp({
  senseStore,
  reduxStore,
}: {
  reduxStore: ReduxStore;
  senseStore: IndexStore;
}) {
  const {env} = reduxStore.getState();

  const [i18nState, setI18nState] = React.useState(labelI18nInstance);
  const [i18nReady, setI18nReady] = React.useState(false);
  const resolvedLanguage = getCurrentLanguageForExternalApps(true, {
    supportBrowserLanguage: false,
    supportLocalStorageLanguage: true,
  });

  React.useEffect(() => {
    const initializeI18n = async () => {
      try {
        await ensureInitialized(resolvedLanguage); // ensure translations are initialized
        setI18nReady(true);
      } catch (error: any) {
        console.error('Error initializing translations:', error);
        setI18nReady(true); // allow app to proceed even if translations fail
      }
    };

    initializeI18n();

    const handleLanguageChanged = () => {
      setI18nState({...labelI18nInstance});
    };

    labelI18nInstance.on('languageChanged', handleLanguageChanged);

    return () => {
      labelI18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!i18nReady) {
    return (
      <div className={styles.i18nLoader}>
        <CircularLoader size="large" />
      </div>
    );
  }

  const content = (
    <MouseTipRoot>
      <div className={styles.body}>
        <ErrorBoundary>
          <AppRoutes senseStore={senseStore} reduxStore={reduxStore} />
        </ErrorBoundary>
      </div>
      <ContextMenuRoot />
      <ModalRoot />
    </MouseTipRoot>
  );

  return (
    // @ts-expect-error - TS2786 - 'Provider' cannot be used as a JSX component.
    <Provider store={reduxStore}>
      <FluxRoot store={senseStore}>
        <I18nContext.Provider value={{labelI18nInstance}}>
          {/* @ts-expect-error - TS2786 - 'Helmet' cannot be used as a JSX component. */}
          <Helmet defaultTitle="Sense" titleTemplate="%s | Sense">
            {isDevOrStage({env}) ? (
              <link href="/images/favicon-dev.svg" rel="icon" />
            ) : (
              <link href="/images/favicon.svg" rel="icon" />
            )}
          </Helmet>
          {process.env.EX_ENV === 'server' ? (
            content
          ) : (
            // @ts-expect-error - TS2786 - 'DndProvider' cannot be used as a JSX component.
            <DndProvider backend={HTML5Backend}>
              {content}
              <DragLayer />
            </DndProvider>
          )}
          <ToastContainer />
          <GenesisToastContainer />
        </I18nContext.Provider>
      </FluxRoot>
    </Provider>
  );
}
